import { Box, Button, Group, LoadingOverlay, Modal, Text } from '@mantine/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { extractErrorMessage } from '@/api/helpers/extractErrorMessage'
import { useRegisterNestedModal } from '@/components/ui-shared/NestedModal/NestedModalContext'
import {
  useDeleteMilestoneServer,
  useInvalidateMilestoneServers
} from '@/queries/vlmQueries'
import { showToast } from '@/theme/notifications'
import { MilestoneServer } from '@/types/vlm'

type MilestoneServerDeleteModalProps = {
  opened: boolean
  server: MilestoneServer | null
  onDeleted?: () => void
  onClose: () => void
}

export const MilestoneServerDeleteModal = ({
  opened,
  server,
  onDeleted,
  onClose
}: MilestoneServerDeleteModalProps) => {
  const intl = useIntl()

  useRegisterNestedModal(opened)

  const { mutateAsync: deleteServer, isPending } = useDeleteMilestoneServer()
  const { invalidateMilestoneServerList } = useInvalidateMilestoneServers()

  const handleDelete = async () => {
    if (!server) return

    try {
      await deleteServer(server.id)
      void invalidateMilestoneServerList()
      onDeleted?.()
      onClose()
    } catch (err) {
      showToast(
        extractErrorMessage(
          err,
          intl.formatMessage({ id: 'vlm.milestoneServers.delete.error' })
        ),
        'error'
      )
    }
  }

  return (
    <Modal
      size="lg"
      title={<FormattedMessage id="vlm.milestoneServers.delete.title" />}
      opened={opened}
      onClose={onClose}
    >
      <Box pos="relative">
        <LoadingOverlay visible={isPending} />

        <Text size="sm">
          <FormattedMessage
            id="vlm.milestoneServers.delete.confirmation"
            values={{ name: <strong>{server?.name}</strong> }}
          />
        </Text>

        <Group justify="end" mt="xl">
          <Button miw={120} variant="default" onClick={onClose}>
            <FormattedMessage id="cancel" />
          </Button>

          <Button miw={120} color="red" onClick={() => void handleDelete()}>
            <FormattedMessage id="vlm.milestoneServers.delete.confirm" />
          </Button>
        </Group>
      </Box>
    </Modal>
  )
}
